import { JsonFormsCore, JsonSchema } from '@jsonforms/core'
import React, { Dispatch, SetStateAction } from 'react'
import { z } from 'zod'
import { Copy } from '../../../elements'
import { useFeature } from '../../../hooks'
import { JsonForm } from '../../json-forms/components'
import { formatGmailAuthorizationUrl, formatLinearAuthorizationUrl } from '../services'
import { CustomForm, GmailForm, OAuthForm, IntegrationForm, WebchatForm } from './CustomForms'
import semver from 'semver'
import { BooleanSwitch } from '~/features/integrations/components/BooleanSwitch'
import { Integration } from 'botpress-client'

type ConfigurationFormProps = {
  iconUrl?: string
  data: Record<string, any>
  setData: Dispatch<SetStateAction<Record<string, any>>>
  setErrors?: (errors: any) => void
  readonly?: boolean
  schema?: JsonSchema
  webhookUrl?: string
  webhookId?: string
  linkTemplateScript?: string
  identifierRequired?: boolean
  identifier?: string
  workspaceId: string
  botId: string
  integration: Integration
}
export const ConfigurationForm = ({
  webhookUrl,
  setData,
  setErrors,
  webhookId,
  linkTemplateScript,
  iconUrl,
  identifier,
  identifierRequired,
  integration,
  ...props
}: ConfigurationFormProps) => {
  const { name: integrationName, version: integrationVersion } = integration
  const slackNewForm = useFeature('slackNewForm')

  if (integrationName === 'webchat') {
    return <WebchatForm webhookUrl={webhookUrl} setData={setData} {...props} />
  }

  if (integrationName === 'gmail') {
    return <GmailForm webhookUrl={webhookUrl} formatAuthorizationUrl={formatGmailAuthorizationUrl} />
  }

  if (
    ['meta/whatsapp', 'whatsapp'].includes(integrationName) &&
    semver.gte(integrationVersion as string, '2.0.0') &&
    !props.data.useManualConfiguration
  ) {
    return (
      <>
        <IntegrationForm
          webhookId={webhookId}
          linkTemplateScript={linkTemplateScript}
          iconUrl={iconUrl}
          setData={setData}
          identifier={undefined}
          identifierRequired={true}
          setErrors={setErrors}
          data={props.data}
          readonly={props.readonly}
          schema={props.schema}
        />
        <BooleanSwitch
          booleanKey={'useManualConfiguration'}
          setData={setData}
          integration={integration}
          data={props.data}
        />
      </>
    )
  }

  if (integrationName === 'slack' && slackNewForm) {
    return (
      <IntegrationForm
        webhookId={webhookId}
        linkTemplateScript={linkTemplateScript}
        iconUrl={iconUrl}
        setData={setData}
        identifier={identifier}
        identifierRequired={identifierRequired}
        setErrors={setErrors}
        data={props.data}
        readonly={props.readonly}
        schema={props.schema}
      />
    )
  }

  if (integrationName === 'linear') {
    return <OAuthForm webhookUrl={webhookUrl} formatAuthorizationUrl={formatLinearAuthorizationUrl} />
  }

  if (CustomFormJsonSchema.safeParse(props.schema).success) {
    return (
      <>
        {webhookUrl && <Copy className="mb-8" label="Webhook URL" value={webhookUrl} description="Webhook URL" />}
        <CustomForm setData={setData} {...props} />
      </>
    )
  }

  return (
    <>
      {webhookUrl && <Copy className="mb-8" label="Webhook URL" value={webhookUrl} description="Webhook URL" />}
      <JsonForm
        onChange={({ data: newData, errors }: Pick<JsonFormsCore, 'data' | 'errors'>) => {
          setErrors?.(errors)
          setData?.(newData)
        }}
        {...props}
      />
    </>
  )
}

const CustomFormJsonSchema = z.object({
  type: z.literal('object').optional(),
  properties: z.object({}).optional(),
  additionalProperties: z.literal(true).optional(),
})
